import React, {useEffect, useMemo, useState} from 'react';
import {EventType} from "../../../../../../models/playlist-model";
import {Button, Dropdown} from "semantic-ui-react";
import DatePicker from "react-datepicker";
import {
    useActiveProjectCalendarsSelector,
    useActiveProjectSelector,
    useCompletedPlaylistItemsMapSelector,
    useCpmMapSelector,
    useCurrentPlaylistItemSelector,
    useLivePlaylistUpdateCountSelector,
    useSkippedPlaylistItemsMapSelector
} from "../../../../../../store/selectors/project.selectors";
import {useUserSelector} from "../../../../../../store/selectors/authorization.selectors";
import {useDispatch} from "react-redux";
import * as projectActions from "../../../../../../store/actions/project.actions";
import styles from "./TaskContainer.module.scss"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/pro-solid-svg-icons";
import {useAllTaskListSelector} from "../../../../../../store/selectors/task/task.selectors";
import {
    convertDateToIndex,
    convertIndexToSeconds
} from "../../../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import CpmFunctionsController from "../../../../../../utils/cpm-functions/cpm-functions.controller";
import moment from "moment";
import FirebaseUsage from "../../../../../../firebase/firebase.usage";
import {Timestamp} from "firebase/firestore";

export default function AutoPilotTaskContainer() {
    const currentPlaylistItem = useCurrentPlaylistItemSelector();
    const skippedPlaylistItems = useSkippedPlaylistItemsMapSelector();
    const user = useUserSelector();
    const livePlaylistUpdateCount = useLivePlaylistUpdateCountSelector();
    const dispatch = useDispatch();
    const [eventTimestamp, setEventTimestamp] = useState<Timestamp | null>(null);
    const [eventType, setEventType] = useState<EventType>(currentPlaylistItem.eventType);
    const [showPositiveDatePicker, setShowPositiveDatePicker] = useState<boolean>(false);
    const [showNegativeDatePicker, setShowNegativeDatePicker] = useState<boolean>(false);
    const allTasksMap = useAllTaskListSelector();
    const task = allTasksMap.get(currentPlaylistItem.taskId);
    const projectCalendars = useActiveProjectCalendarsSelector();
    const cpmMap = useCpmMapSelector();
    const cpmTask = task ? cpmMap.get(task.task_id) : null;
    const activeProject = useActiveProjectSelector();
    const [issueDropdownValue, setIssueDropdownValue] = useState<string | null>(null);
    const [commentValue, setCommentValue] = useState<string | null>(null);
    const [progressValue, setProgressValue] = useState<number | null>(0);
    const completedPlaylistItems = useCompletedPlaylistItemsMapSelector();

    useEffect(() => {
        setEventType(currentPlaylistItem.eventType);
    }, [currentPlaylistItem])

    const skipPlaylistItem = () => {
        skippedPlaylistItems.set(`${currentPlaylistItem.taskCode}-${currentPlaylistItem.eventType}`, user!.userId);
        dispatch(projectActions.Actions.setLivePlaylistUpdateCount(livePlaylistUpdateCount + 1));
    }

    const autoPilotPrompt = (eventType: EventType) => {
        switch (eventType) {
            case EventType.UNBLOCK:
                return "Has this task been unblocked?"
            case EventType.RESUME:
                return "Has this task been resumed?"
            case EventType.FINISH:
                return "Has this task finished?"
            case EventType.START:
                return "Has this task started?"
            case EventType.PROGRESS:
                return "Has this task progressed?"
            default:
                return "What's happening with this task?"
        }
    }

    const datePositivePrompt = () => {
        switch (eventType) {
            case EventType.FINISH:
                return "When did this task finish?"
            case EventType.START:
                return "When did this task start?"
            case EventType.RESUME:
                return "When did this task resume?"
            case EventType.UNBLOCK:
                return "When did this task become unblocked?"
            default:
                return "When did this event occur?"
        }
    }

    const dateNegativePrompt = () => {
        switch (eventType) {
            case EventType.EXTEND:
                return "When will this task finish?"
            case EventType.REFORECAST:
                return "When will this task start?"
            case EventType.SUSPEND:
                return "When will this task resume?"
            case EventType.LOG_ISSUE:
                return "When did this issue occur?"
            case EventType.BLOCK:
                return "When will this task become unblocked?"
            default:
                return "When did this event occur?"
        }
    }

    const selectMinDate = () => {
        switch (eventType) {
            case EventType.FINISH:
                return task?.act_start_date?.toDate() || null
            case EventType.REFORECAST:
                return cpmTask ? new Date((convertIndexToSeconds(convertDateToIndex((Math.ceil(new Date().getTime() / 86400000) * 86400000) / 1000,
                    cpmTask.cal_id, projectCalendars) - 1, cpmTask.cal_id, projectCalendars) + 1800) * 1000 + (new Date().getTimezoneOffset() * 60 * 1000)) : new Date()
            default:
                return null
        }
    }

    const selectMaxDate = () => {
        switch (eventType) {
            case EventType.START:
            case EventType.RESUME:
                return cpmTask ? new Date(convertIndexToSeconds(convertDateToIndex((Math.floor(new Date().getTime() / 86400000) * 86400000) / 1000,
                    cpmTask.cal_id, projectCalendars), cpmTask.cal_id, projectCalendars) * 1000 + (new Date().getTimezoneOffset() * 60 * 1000)) : new Date();
            default:
                return null;
        }
    }

    const selectCurrentDate = () => {
        switch (eventType) {
            case EventType.FINISH:
            case EventType.EXTEND:
                return cpmTask ? new Date((convertIndexToSeconds(convertDateToIndex((Math.ceil(new Date().getTime() / 86400000) * 86400000) / 1000,
                    cpmTask.cal_id, projectCalendars) - 1, cpmTask.cal_id, projectCalendars) + 1800) * 1000 + (new Date().getTimezoneOffset() * 60 * 1000)) : new Date()
            case EventType.START:
            case EventType.REFORECAST:
            case EventType.SUSPEND:
            case EventType.BLOCK:
                return cpmTask ? new Date(convertIndexToSeconds(convertDateToIndex((Math.floor(new Date().getTime() / 86400000) * 86400000) / 1000,
                    cpmTask.cal_id, projectCalendars), cpmTask.cal_id, projectCalendars) * 1000 + (new Date().getTimezoneOffset() * 60 * 1000)) : new Date();
            default:
                return new Date()
        }
    }

    useEffect(() => {
        setEventTimestamp(FirebaseUsage.timestamp(selectCurrentDate()));
    }, [eventType]);

    const resetEventTypeOnNo = () => {
        switch (eventType) {
            case EventType.FINISH:
                setEventType(EventType.EXTEND);
                break;
            case EventType.START:
                setEventType(EventType.REFORECAST);
                break;
            case EventType.RESUME:
                setEventType(EventType.SUSPEND);
                break;
            case EventType.UNBLOCK:
                setEventType(EventType.BLOCK);
                break;
            case EventType.PROGRESS:
                setEventType(EventType.LOG_ISSUE);
                break;
            default:
                break;
        }
    }

    const headlineText = () => {
        switch (eventType) {
            case EventType.FINISH:
                return "FINISHING TASK"
            case EventType.START:
                return "STARTING TASK"
            case EventType.RESUME:
                return "RESUMING TASK"
            case EventType.UNBLOCK:
                return "UNBLOCKING TASK"
            case EventType.REFORECAST:
                return "REFORECASTING TASK"
            case EventType.EXTEND:
                return "EXTENDING TASK"
            case EventType.SUSPEND:
                return "SUSPENDING TASK"
            case EventType.LOG_ISSUE:
                return "LOGGING A PROGRESS ISSUE"
            case EventType.BLOCK:
                return "BLOCKING TASK"
            default:
                return "Task event"
        }
    }

    const showCommentBox = () => {
        switch (eventType) {
            case EventType.REFORECAST:
            case EventType.SUSPEND:
            case EventType.LOG_ISSUE:
            case EventType.PROGRESS:
            case EventType.BLOCK:
            case EventType.EXTEND:
                return true;
            default:
                return false;
        }
    }

    const showIssueDropdown = () => {
        switch (eventType) {
            case EventType.REFORECAST:
            case EventType.SUSPEND:
            case EventType.LOG_ISSUE:
            case EventType.BLOCK:
            case EventType.EXTEND:
                return true;
            default:
                return false;
        }
    }

    const issueDropDownOptions = useMemo(() => {
        if (!activeProject) return [];
        return activeProject.issueCategories.map((issue) => ({
            key: issue.reason,
            text: issue.text,
            value: issue.text
        }))
    }, [activeProject])

    const commentBox = () => {
        return (
            <div className={styles.AutoPilotTextInputContainer}>
                {showIssueDropdown() && <Dropdown
                    search
                    placeholder="Log issue"
                    className="compact-dropdown"
                    selection
                    onChange={(_, {value}) => setIssueDropdownValue(value as string)}
                    options={issueDropDownOptions}/>}
                <textarea
                    onChange={(e) => setCommentValue(e.target.value)}
                    className={styles.AutoPilotTextInput} placeholder="Add a comment..." />
            </div>
        )
    }

    const dateInput = (currentDate: Date, minDate: Date | null, maxDate: Date | null) => {
        return (<DatePicker
            selected={eventTimestamp?.toDate() || currentDate}
            onChange={(date: Date) => setEventTimestamp(Timestamp.fromDate(date))}
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
            dateFormat="d MMMM yyyy h:mm aa"
        />)
    }

    const positiveButton = () => {
        return (
            <div className={styles.PositiveButtonContainer}>
                Confirm date
                {dateInput(selectCurrentDate(), selectMinDate(), selectMaxDate())}
                {progressInput()}
                {showCommentBox() && commentBox()}
                <div className={styles.PositiveButtonActions}>
                    <Button positive onClick={() => {
                        setShowPositiveDatePicker(false);
                        handleConfirm().catch(e => console.error("Error confirming autopilot action:", e));
                    }}>Confirm</Button>
                    <Button onClick={() => setShowPositiveDatePicker(false)}>Cancel</Button>
                </div>

            </div>
        )
    }

    const negativeButtonAlternativeSelection = () => {
        switch (eventType) {
            case EventType.EXTEND:
                return (<Button primary onClick={() => setEventType(EventType.SUSPEND)}>Suspend</Button>)
            case EventType.SUSPEND:
                return (<Button primary onClick={() => setEventType(EventType.EXTEND)}>Extend</Button>)
            case EventType.REFORECAST:
                return (<Button primary onClick={() => setEventType(EventType.BLOCK)}>Block</Button>)
            default:
                return null;
        }
    }

    const taskProgress = useMemo(() => {
        if (!task) return 0;
        if (task.progress && task.progress.length > 0) {
            return task.progress.sort((a, b) =>
                b.date.seconds - a.date.seconds)[0].progress
        }
        return 0;
    }, [task])

    useEffect(() => {
        setProgressValue(taskProgress);
    }, [taskProgress]);

    const progressInput = () => {
        switch (eventType) {
            case EventType.PROGRESS:
            case EventType.EXTEND:
                return (
                    <div className={styles.AutoPilotProgressInputContainer}>
                        <input
                            type="number"
                            min={0}
                            max={100}
                            value={progressValue ?? 0}
                            onChange={(e) =>
                                setProgressValue(Number(e.target.value))
                            }
                            className={styles.AutoPilotProgressInput}
                            placeholder="Enter progress percentage..."
                        />
                        <div className={styles.AutoPilotProgressInputPercentage}>
                            % complete
                        </div>
                    </div>
                )
            default:
                return null;
        }
    }

    const negativeButton = () => {
        return (
            <div className={styles.PositiveButtonContainer}>
                Confirm date
                {dateInput(selectCurrentDate(), selectMinDate(), selectMaxDate())}
                {progressInput()}
                {showCommentBox() && commentBox()}
                <div className={styles.PositiveButtonActions}>
                    <Button positive onClick={(e) => {
                        e.stopPropagation();
                        setShowNegativeDatePicker(false);
                        handleConfirm().catch(e => console.error("Error confirming autopilot action:", e));
                    }}>Confirm</Button>
                    {negativeButtonAlternativeSelection()}
                    <Button onClick={(e) => {
                        e.stopPropagation();
                        setEventType(currentPlaylistItem.eventType);
                        setShowNegativeDatePicker(false)
                    }}>Cancel</Button>
                </div>
            </div>
        )
    }

    const autoPilotButtons = (eventType: EventType) => {
        return !showPositiveDatePicker && !showNegativeDatePicker ? (
            <div className={styles.AutoPilotButtons}>
                <Button disabled={showPositiveDatePicker} positive onClick={(e) => {
                    e.stopPropagation();
                    setShowPositiveDatePicker(true)}}>Yes</Button>
                <Button disabled={showPositiveDatePicker} onClick={(e) => {
                    e.stopPropagation();
                    resetEventTypeOnNo();
                    setShowNegativeDatePicker(true);
                }}>No</Button>
                <Button disabled={showPositiveDatePicker} primary onClick={skipPlaylistItem}>Skip</Button>
            </div>
        ) : null
    }

    const handleConfirm = async () => {
        if (activeProject && user && task) {
            await CpmFunctionsController.runAutoPilotAction({
                project_id: activeProject.projectId,
                contract_id: activeProject.contractId,
                task_id: task.task_id,
                event_type: eventType,
                suspend_reason: issueDropdownValue,
                reason_text: commentValue,
                event_prompt: currentPlaylistItem.eventType,
                seconds: eventTimestamp?.seconds || Math.floor(new Date().getTime() / 1000),
                expiry_date: eventTimestamp?.seconds || Math.floor(new Date().getTime() / 1000),
                resume_date: eventTimestamp?.toDate() || new Date(),
                data_date: activeProject.frozenDate ? Math.floor(activeProject.frozenDate.seconds / 86400) * 86400 : null,
                sprint_id: activeProject.snapshotDataVersionId,
                data_version_id: activeProject.snapshotDataVersionId || 'no data version id',
                progress: progressValue || 0,
                progress_changed: progressValue !== null && progressValue !== taskProgress,
                user_id: user.userId || 'no user id',
                task,
                date_string: moment().format("YYYY-MM-DD"),
                user_email: user.userEmail || 'no user email',
                task_code: task.task_code || 'no task code',
            }, dispatch)
        }
    }

    return (
        <div className={styles.AutoPilotContainer} onClick={(e) => e.stopPropagation()}>
            {(showPositiveDatePicker || showNegativeDatePicker) && <strong>{headlineText()}</strong>}
            {!showPositiveDatePicker && !showNegativeDatePicker && autoPilotPrompt(currentPlaylistItem.eventType)}
            {showPositiveDatePicker && datePositivePrompt()}
            {showNegativeDatePicker && dateNegativePrompt()}
            {autoPilotButtons(currentPlaylistItem.eventType)}
            <div className={styles.AutoPilotCloseIcon} onClick={() =>
                dispatch(projectActions.Actions.setCurrentPlaylistItem({...currentPlaylistItem, paused: true}))}>
                <FontAwesomeIcon icon={faTimes} />
            </div>
            {showPositiveDatePicker && positiveButton()}
            {showNegativeDatePicker && negativeButton()}
        </div>
    );
}